import { createSelector } from 'reselect'
import isPlainObject from 'lodash/isPlainObject'
import omit from 'lodash/omit'
import { pipe, assocPath, dissocPath } from 'ramda'
import Immutable from 'seamless-immutable'

import moment from '../Core/Moment'
import { checkIfShouldBeDeleted } from '../Core/ContainerFunctions'

import { selectUser, selectVehicleType, selectPolicyHolder, selectAllUserData, selectBonusType } from './userData'
import { selectAppData } from './common'
import { selectSpecification, selectArrayTypes } from './product'
import { isUndefined } from '../utils'

import type { Values } from '../Core/Types/Values'
import type { ExpandedSpecificationDTO } from '../Core/Types/Field'
import type { ArrayFieldFormTypes } from '../Core/Types/ArrayFieldFormType'
import type QuoteInstance from '../Core/Controllers/QuoteInstance'

const selectValidation = (state) => selectAppData(state).validation
const selectValidationErrors = (state) => selectValidation(state).validationErrors
const selectPolicyHolderBirthDateErrorType = (state) => {
  return selectValidationErrors(state).getIn(['policyHolder', 'birthDate', 'error_type'])
}

const selectPolicyHolderPhoneErrorType = (state) => {
  return selectValidationErrors(state).getIn(['policyHolder', 'phoneNumber', 'error_type'])
}

const selectUserBirthDateErrorType = (state) => {
  return selectValidationErrors(state).getIn(['user', 'birthDate', 'error_type'])
}

const selectPolicyHolderBirthDateValidation = createSelector(
  [selectVehicleType, selectPolicyHolder, selectPolicyHolderBirthDateErrorType],
  (type, policyHolder, policyHolderErrorType) => {
    if (policyHolderErrorType === 'date_range_error') {
      const yearsDiffPC = moment().diff(moment(policyHolder.birthDate), 'years')
      if (type === 'car') {
        return yearsDiffPC < 18 ? policyHolderErrorType : 'policy_holder_date_range_error_other'
      } else if (type === 'motorcycle') {
        return yearsDiffPC < 15 ? 'policy_holder_date_range_error_moto15' : 'policy_holder_date_range_error_moto18'
      } else {
        return 'date_range_error_other'
      }
    }

    return policyHolderErrorType
  },
)

const selectUserBirthDateValidation = createSelector(
  [selectVehicleType, selectUser, selectUserBirthDateErrorType],
  (type, user, userErrorType) => {
    if (userErrorType === 'date_range_error') {
      const yearsDiffU = moment().diff(moment(user.birthDate), 'years')
      if (type === 'motorcycle' && yearsDiffU < 15) {
        return 'user_date_range_error_moto15'
      }
    }
    return userErrorType
  },
)

const selectPolicyHolderPhoneValidation = createSelector(
  [selectBonusType, selectPolicyHolderPhoneErrorType],
  (bonusType, phoneErrorType) => {
    if (bonusType !== 'notSure' && phoneErrorType === 'required_field_missing') {
      return 'required_for_private_offers'
    }
    return phoneErrorType
  },
)

export const selectAdjustedValidationErrors = createSelector(
  [
    selectAllUserData,
    selectValidationErrors,
    selectPolicyHolderBirthDateValidation,
    selectUserBirthDateValidation,
    selectPolicyHolderPhoneValidation,
  ],
  (
    userData,
    validationErrors,
    policyHolderBirthDateValidation,
    userBirthDateValidation,
    policyHolderPhoneValidation,
  ) => {
    return pipe(
      !isUndefined(policyHolderBirthDateValidation)
        ? assocPath(['policyHolder', 'birthDate', 'error_type'], policyHolderBirthDateValidation)
        : dissocPath(['policyHolder', 'birthDate']),
      !isUndefined(userBirthDateValidation)
        ? assocPath(['user', 'birthDate', 'error_type'], userBirthDateValidation)
        : dissocPath(['user', 'birthDate']),
      !isUndefined(policyHolderPhoneValidation)
        ? assocPath(['policyHolder', 'phoneNumber', 'error_type'], policyHolderPhoneValidation)
        : dissocPath(['policyHolder', 'phoneNumber']),
    )(validationErrors)
  },
)

function createProductDescriptorValues(
  values: Values,
  specification: ExpandedSpecificationDTO,
  arrayTypes: ArrayFieldFormTypes,
  removeDisabled: boolean = true,
  quote?: QuoteInstance,
  deleteRegExp: boolean | RegExp = false,
) {
  if (values && specification && specification.fields) {
    const result = specification.fields.reduce((acc, field) => {
      const {
        domain: { _type, specification: fieldSpecification },
        name,
      } = field
      const value = values[name]

      if (
        !isUndefined(value) &&
        field.enabled &&
        value !== '' &&
        (deleteRegExp === false ||
          (deleteRegExp instanceof RegExp &&
            !checkIfShouldBeDeleted(field.name, value, isPlainObject(value), deleteRegExp)))
      ) {
        if (_type.startsWith('int')) {
          return { ...acc, [field.name]: parseInt(value, 10) }
        } else if (_type === 'float') {
          return { ...acc, [field.name]: parseFloat(value) }
        } else if (_type === 'array' && Array.isArray(value)) {
          return {
            ...acc,
            [field.name]: arrayTypes[field.name]
              ? [...value.slice(0, arrayTypes[field.name].length)]
              : []
                  // $FlowIssue
                  .map((arrayValue) => {
                    return createProductDescriptorValues(
                      arrayValue,
                      fieldSpecification,
                      arrayTypes,
                      removeDisabled,
                      quote,
                      deleteRegExp,
                    )
                  }),
          }
        } else if (_type === 'object' && isPlainObject(value)) {
          return {
            ...acc,
            [field.name]: createProductDescriptorValues(
              value,
              fieldSpecification,
              arrayTypes,
              removeDisabled,
              quote,
              deleteRegExp,
            ),
          }
        } else {
          return { ...acc, [field.name]: value }
        }
      }

      return acc
    }, {})
    return result
  }
  return {}
}

function handleCic(values, quote?: QuoteInstance) {
  return (productDescriptorValues) => {
    if (!values.cicEnabled || (quote && !quote.subProductToTariffName.cic)) {
      return omit(productDescriptorValues, 'cic')
    }

    if (values.cicEnabled && productDescriptorValues.cic) {
      return { ...productDescriptorValues, cicEnabled: true }
    }

    return productDescriptorValues
  }
}

export const selectProductDescriptorValues = createSelector(
  [selectSpecification, selectAllUserData, selectArrayTypes],
  (specification, userData, arrayTypes) => {
    return pipe(
      () => createProductDescriptorValues(userData, specification, arrayTypes),
      handleCic(userData),
      (productDescriptorValues) => Immutable.asMutable(productDescriptorValues, { deep: true }),
    )()
  },
)
